import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles, categories, hotRank } from "@/mocks/content";
import ArticleCard from "@/components/feature/ArticleCard";

const tagCloud = ["聊天记录", "时间线", "官方回应", "人设反差", "选品价格", "品牌合作", "取关", "直播事故"];

export default function FeedSection() {
  const [active, setActive] = useState<string>("all");

  const list = useMemo(
    () => (active === "all" ? articles : articles.filter((item) => item.category === active)),
    [active],
  );

  return (
    <section id="feed" aria-labelledby="feed-title" className="w-full bg-background-100/60">
      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="feed-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                今日瓜速递
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                按发布时间倒序，最新爆料排在最前面
              </p>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
              </span>
              正在更新中
            </span>
          </div>

          <div className="no-scrollbar -mx-1 flex items-center gap-1 overflow-x-auto px-1 py-1">
            <button
              type="button"
              onClick={() => setActive("all")}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                active === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                onClick={() => setActive(category.slug)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  active === category.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {list.length > 0 ? (
              <div className="flex flex-col gap-3.5">
                {list.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
                这个分类暂时还没有新瓜，换个分类看看？
              </p>
            )}
            <Link
              to="/news"
              className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-background-300/80 bg-background-50 py-3 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:border-primary-400 hover:text-primary-600"
            >
              查看全部瓜料
              <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
            </Link>
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div id="rank" className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-500 text-background-50">
                  <i className="ri-fire-fill text-xs" aria-hidden="true" />
                </span>
                吃瓜热榜
              </h3>
              <ol className="mt-4 flex flex-col">
                {hotRank.map((item, index) => {
                  const target = articles.find((article) => article.id === item.id);
                  return (
                    <li key={item.id} className="border-b border-background-200/70 last:border-0">
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex items-start gap-3 py-3"
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="clamp-2 block text-sm font-medium leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">
                            热度 {item.heat}
                            {target ? ` · ${target.tags[0]}` : ""}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">热门标签</h3>
              <div className="mt-3.5 flex flex-wrap gap-2">
                {tagCloud.map((tag) => (
                  <Link
                    key={tag}
                    to={`/news?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors duration-200 hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
              <Link
                to="/about#tips"
                className="mt-5 flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-foreground-950 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-foreground-800"
              >
                <i className="ri-send-plane-line text-base" aria-hidden="true" />
                我要爆料投稿
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}